/**
 * Place search client.
 *
 *   GET /v1/places/suggest?q=<text>  suggestions while typing (local place
 *                                    names + OpenStreetMap type-ahead)
 *   GET /v1/places/search?q=<text>   full search, when the person presses
 *                                    Enter or picks "Search for …"
 *
 * Public endpoints (no auth): guests use them too. The backend constrains
 * results to the tenant's geography and rate-limits per IP.
 */
import { ApiError, apiBaseUrl, apiFetch, fetchWithAuth } from './api';

export interface PlaceResult {
  name: string;
  lat: number;
  lon: number;
  label: string;
  /** "local" = the jurisdiction's own place names; "osm" = OpenStreetMap. */
  source?: string;
  /** [west, south, east, north] when the place is an area. */
  extent?: [number, number, number, number] | null;
}

export async function searchPlaces(query: string): Promise<PlaceResult[]> {
  const params = new URLSearchParams({ q: query });
  return apiFetch<PlaceResult[]>(`/v1/places/search?${params.toString()}`);
}

export interface PlaceSuggestions {
  places: PlaceResult[];
  /**
   * Street/address suggestions (OpenStreetMap): "ok", "disabled" (not set up
   * for this site), or "unavailable" (the service couldn't be reached).
   */
  streets: 'ok' | 'disabled' | 'unavailable';
}

export async function suggestPlaces(query: string, lang: string): Promise<PlaceSuggestions> {
  const params = new URLSearchParams({ q: query, lang: lang.slice(0, 2) });
  const response = await fetchWithAuth(`${apiBaseUrl()}/v1/places/suggest?${params.toString()}`);
  if (!response.ok) throw new ApiError(response.status, `http_${response.status}`, 'Suggestions failed.');
  const places = (await response.json()) as PlaceResult[];
  const header = response.headers.get('X-Place-Suggestions');
  const streets = header === 'disabled' || header === 'unavailable' ? header : 'ok';
  return { places: Array.isArray(places) ? places : [], streets };
}
